import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { CerrixAvatarGroupComponent } from "@app/shared/molecules/cerrix-avatar-group/cerrix-avatar-group.component";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { CerrixDialogComponent } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog.component";
import { CerrixDialogService } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog-service.component";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { CerrixLabelComponent } from "@app/shared/atoms/cerrix-label/cerrix-label.component";
import { CerrixUserPickerComponent } from "@app/shared/organisms/cerrix-user-picker/cerrix-user-picker.component";
import { IdGuidNameCombination } from "@models/generic/IdGuidNameCombination";
import { provideTranslocoScope, TranslocoPipe } from "@jsverse/transloco";

@Component({
    selector: "app-asset-owner-selector",
    templateUrl: "./asset-owner-selector.component.html",
    styleUrls: ["./asset-owner-selector.component.scss"],
    imports: [
        CerrixAvatarGroupComponent,
        CerrixButtonComponent,
        CerrixDialogComponent,
        CerrixInstructionsIconComponent,
        CerrixLabelComponent,
        CerrixUserPickerComponent,
        TranslocoPipe,
    ],
    providers: [provideTranslocoScope("assetmanagement")],
    encapsulation: ViewEncapsulation.None,
})
export class AssetOwnerSelectorComponent implements OnChanges {
    @Input() users: IdGuidNameCombination[] = [];
    @Input() ownerId: string;
    @Input() readonly: boolean;

    @Output() ownerIdChange: EventEmitter<string> = new EventEmitter<string>();

    protected readonly ownerDialogName = "asset-owner-dialog";

    /** The pick inside the dialog, committed to ownerId only on confirm. */
    protected draftOwnerId: string;

    /** The owner as the avatar group wants it: a list, held as one field so the
     *  OnPush group isn't handed a fresh array on every change detection run. */
    protected ownerNames: string[] = [];

    private _dialogService = inject(CerrixDialogService);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["ownerId"] || changes["users"]) {
            this.ownerNames = this.ownerNamesFor(this.ownerId);
        }
    }

    protected openOwnerDialog(): void {
        this.draftOwnerId = this.ownerId;
        this._dialogService.openDialog(this.ownerDialogName);
    }

    protected cancelOwnerSelection(): void {
        this._dialogService.closeDialog(this.ownerDialogName);
    }

    protected confirmOwnerSelection(): void {
        this.ownerId = this.draftOwnerId;
        this.ownerNames = this.ownerNamesFor(this.ownerId);
        this.ownerIdChange.emit(this.ownerId);
        this._dialogService.closeDialog(this.ownerDialogName);
    }

    private ownerNamesFor(ownerId: string): string[] {
        const owner = this.users.find((user) => user.Guid === ownerId);
        return owner ? [owner.Name] : [];
    }
}
